import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Trans, useTranslation } from 'react-i18next';

// Story 065 — Install instructions modal.
// Shown when the user clicks Install but the browser can't fire a native
// prompt — either iOS Safari (no programmatic install at all) or some other
// browser without PWA install support (Firefox desktop, Safari desktop).
// We adapt the copy by user-agent so the user sees relevant instructions.

interface Props {
  open: boolean;
  brand: string;
  onClose: () => void;
}

type Platform = 'ios-safari' | 'ios-other' | 'android-chromium' | 'other';

function detectPlatform(): Platform {
  if (typeof navigator === 'undefined') return 'other';
  const ua = navigator.userAgent;
  const isIos = /iPad|iPhone|iPod/.test(ua);
  if (isIos) {
    // Chrome iOS = CriOS; Firefox iOS = FxiOS. Both are still WebKit
    // underneath but the Add-to-Home-Screen entry only appears in
    // Safari proper.
    const isOtherBrowser = /CriOS|FxiOS|EdgiOS/.test(ua);
    return isOtherBrowser ? 'ios-other' : 'ios-safari';
  }
  // Android + a Chromium-family browser (Chrome, Edge, Brave, Samsung
  // Internet) — install IS supported here, but if `beforeinstallprompt`
  // hasn't fired yet (engagement criteria not met), the user can install
  // manually via the browser menu.
  const isAndroid = /Android/.test(ua);
  const isChromium = /Chrome|Chromium|Edg|SamsungBrowser/.test(ua);
  if (isAndroid && isChromium) return 'android-chromium';
  return 'other';
}

export function InstallInstructionsModal({ open, brand, onClose }: Props) {
  const { t } = useTranslation('common');
  const platform = detectPlatform();
  const emphasis = <span className="font-medium" />;

  const descriptionKey =
    platform === 'ios-safari'
      ? 'install_instructions.description_ios_safari'
      : platform === 'ios-other'
        ? 'install_instructions.description_ios_other'
        : platform === 'android-chromium'
          ? 'install_instructions.description_android_chromium'
          : 'install_instructions.description_other';

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <DialogContent data-testid="install-instructions-modal">
        <DialogHeader>
          <DialogTitle>{t('install_instructions.title', { brand })}</DialogTitle>
          <DialogDescription>{t(descriptionKey, { brand })}</DialogDescription>
        </DialogHeader>

        {platform === 'ios-safari' && (
          <ol className="list-decimal pl-6 space-y-2 text-sm">
            <li>
              <Trans
                i18nKey="install_instructions.ios_safari_step_1"
                t={t}
                components={{ share: emphasis }}
              />
            </li>
            <li>
              <Trans
                i18nKey="install_instructions.ios_safari_step_2"
                t={t}
                components={{ add: emphasis }}
              />
            </li>
            <li>{t('install_instructions.ios_safari_step_3')}</li>
          </ol>
        )}

        {platform === 'ios-other' && (
          <p className="text-sm">
            <Trans
              i18nKey="install_instructions.ios_other"
              t={t}
              components={{ safari: emphasis, add: emphasis }}
            />
          </p>
        )}

        {platform === 'android-chromium' && (
          <>
            <ol className="list-decimal pl-6 space-y-2 text-sm">
              <li>
                <Trans
                  i18nKey="install_instructions.android_step_1"
                  t={t}
                  components={{ menu: emphasis }}
                />
              </li>
              <li>
                <Trans
                  i18nKey="install_instructions.android_step_2"
                  t={t}
                  components={{ install: emphasis, add: emphasis }}
                />
              </li>
              <li>{t('install_instructions.android_step_3')}</li>
            </ol>
            <p className="text-xs text-muted-foreground">
              {t('install_instructions.android_note')}
            </p>
          </>
        )}

        {platform === 'other' && (
          <p className="text-sm">
            <Trans
              i18nKey="install_instructions.other"
              t={t}
              values={{ brand }}
              components={{ chrome: emphasis, safari: emphasis }}
            />
          </p>
        )}

        <DialogFooter>
          <Button onClick={onClose}>{t('install_instructions.got_it')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
